'use client';

import React from 'react';
import { Box, Typography, Button, Dialog } from 'src/components';
import { useDeleteFilterSetMutation } from 'src/api/redux/filterSets';
import { useNotification } from 'src/context/notification';
import { captureException } from 'src/observability';
import { useAppDispatch, cleanupDeletedColumnReference } from 'src/redux';

interface CodeSetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  volumeToDelete: {
    id: number;
    label: string;
    volumeType?: 'filterSet';
  } | null;
  onDeleteSuccess?: () => void;
}

const DeleteCodeSetDialog: React.FC<CodeSetDialogProps> = ({
  isOpen,
  onClose,
  volumeToDelete,
  onDeleteSuccess,
}) => {
  const { setNotification } = useNotification();
  const dispatch = useAppDispatch();

  // RTK Query mutation hook - cache invalidation happens automatically
  const [deleteFilterSet, { isLoading: isDeleting }] =
    useDeleteFilterSetMutation();

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!volumeToDelete) return;

    try {
      // Call the RTK Query mutation - cache invalidation happens automatically via invalidatesTags
      await deleteFilterSet({
        filterId: volumeToDelete.id,
      }).unwrap();
    } catch (error: unknown) {
      // Show the API's error_message verbatim: it says why the code set can't be deleted
      // (e.g. it is still used by a strategy or AI search). baseQuery already reports 500s.
      const errorMessage = (error as { data?: { error_message?: string } })
        ?.data?.error_message;
      setNotification({
        title: 'Error',
        message: errorMessage || 'Failed to delete code set',
        type: 'error',
      });
      return;
    }

    // The code set is deleted at this point, so a failure below is reported to observability
    // rather than shown to the user as a failed delete.
    try {
      // Clean up any column references to this code set from Redux and localStorage
      dispatch(
        cleanupDeletedColumnReference({
          id: volumeToDelete.id,
          type: 'code_set',
        })
      );

      setNotification({
        title: 'Success',
        message: `Code set "${volumeToDelete.label}" has been deleted`,
        type: 'success',
      });

      onDeleteSuccess?.();
      onClose();
    } catch (error: unknown) {
      captureException(error, {
        tags: { context: 'delete-code-set-post-delete' },
      });
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="delete-filter-set-dialog"
      size="sm"
      hideBackdrop={false}
      className="!h-auto"
    >
      <Dialog.Content>
        <Box className="p-6" onClick={(e) => e.stopPropagation()}>
          <Typography
            data-testid="delete-filter-set-dialog-title"
            className="text-neutral-800 mb-4"
          >
            Are you sure you want to delete code set &quot;
            {volumeToDelete?.label}
            &quot;? This action cannot be undone.
          </Typography>
          <Box className="flex justify-end gap-3 mt-6">
            <Button
              variant="outlined"
              color="neutral"
              onClick={onClose}
              className="px-4"
              data-testid="delete-filter-set-dialog-cancel-button"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              onClick={handleDelete}
              className="px-4"
              data-testid="delete-filter-set-dialog-confirm-button"
              loading={isDeleting}
              disabled={isDeleting}
            >
              Confirm
            </Button>
          </Box>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};

export default DeleteCodeSetDialog;
